import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { commissionService } from "../services/commission.service";
import type {
  CommissionResponseDTO,
  CommissionSearchParams,
  RateType,
} from "../types/commission.types";

interface UseCommissionListOptions {
  sellerId: number | null;
  enabled: boolean;
  initialStartDate: string;
  initialEndDate: string;
  pageSize?: number;
}

export interface CommissionListFilters {
  startDate: string;
  endDate: string;
  type?: RateType[];
}

const DEFAULT_PAGE_SIZE = 10;

export function useCommissionList({
  sellerId,
  enabled,
  initialStartDate,
  initialEndDate,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseCommissionListOptions) {
  const [data, setData] = useState<CommissionResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState<CommissionListFilters>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (targetPage: number) => {
      if (sellerId === null) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const searchParams: CommissionSearchParams = {
          sellerId,
          startDate: filters.startDate,
          endDate: filters.endDate,
          type: filters.type,
          page: targetPage,
          size: pageSize,
          sortBy: "calculatedAt",
          sortDirection: "DESC",
        };
        const response = await commissionService.search(searchParams);
        if (requestIdRef.current !== requestId) return;
        setData(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setPage(targetPage);
      } catch (error: unknown) {
        if (requestIdRef.current !== requestId) return;
        const message = getApiErrorMessage(
          error,
          "Erro ao carregar comissões do vendedor",
        );
        setError(message);
        toast.error(message);
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [sellerId, filters, pageSize],
  );

  useEffect(() => {
    if (!enabled || sellerId === null) return;
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(0);
  }, [enabled, sellerId, load]);

  const removeLocal = useCallback((id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
    setTotalElements((prev) => Math.max(0, prev - 1));
  }, []);

  const updateLocal = useCallback((updated: CommissionResponseDTO) => {
    setData((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item)),
    );
  }, []);

  const reload = useCallback(() => {
    load(page);
  }, [load, page]);

  return {
    data,
    loading,
    error,
    page,
    totalPages,
    totalElements,
    filters,
    setFilters,
    setPage: load,
    reload,
    removeLocal,
    updateLocal,
  };
}
